import React, { FunctionComponent, useContext, useState } from "react";
import { Icon, Modal, ModalBody, TableIconButton, Text, useTranslation } from "@familyzone/component-library";
import { Props as ButtonProps } from "@familyzone/component-library/src/components/Button";
import { ReactRouterCompatContext } from "../components/ContextProvider";
import { InjectedRouter } from "react-router";

interface Props extends Omit<ButtonProps, "variant" | "children"> {
  onClick: (e: React.SyntheticEvent) => void;
  isCopying?: boolean;
  to?: string;
  disabled?: boolean;
  actionVerb?: string; // Manually set the action verb. Eg, Copy
  policyToCopy: string; // The modal body will ask if the user wants to permanently copy this [policyToCopy]
}

export const CopyFilteringButton: FunctionComponent<Props> = ({
  onClick,
  isCopying = false,
  to = undefined,
  disabled = false,
  actionVerb = "Copy",
  policyToCopy,
  ...props
}) => {
  const { router } = useContext(ReactRouterCompatContext) as { router: InjectedRouter };
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleOnOpen = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setOpen(true);
  };

  const handleOnClose = () => {
    setOpen(false);
  };

  const handlePrimaryCTAClick = (e: React.SyntheticEvent) => {
    e.stopPropagation();

    if (to !== undefined) {
      router.push(to);
    }
    handleOnClose();
    onClick(e);
  };

  const titleText = t(`${actionVerb} ${policyToCopy.length > 30 ? policyToCopy.slice(0, 30) + `...` : policyToCopy}`);
  const bodyText = t(
    `Are you sure you want to ${actionVerb.toLowerCase()} this policy ${
      policyToCopy.length > 30 ? policyToCopy.slice(0, 30) + `...` : policyToCopy
    }?`
  );

  return (
    <>
      <TableIconButton
        icon={
          <Icon
            icon={`fa-copy${isCopying ? " fa-spin" : ""}`}
            data-testid={`fa-copy${isCopying ? " fa-spin" : ""}`}
            variant="solid"
            color="text.paragraph.light"
          />
        }
        onClick={handleOnOpen}
        aria-label="Copy"
        disabled={disabled}
        {...props}
      />
      <Modal
        isOpen={open}
        size="sm"
        onClose={handleOnClose}
        headerText={titleText}
        variant="warning"
        primaryCTALabel={titleText}
        onPrimaryCTAClick={handlePrimaryCTAClick}
        secondaryCTALabel="Cancel"
        onSecondaryCTAClick={handleOnClose}
      >
        <ModalBody>
          <Text mb="48px">{bodyText}</Text>
        </ModalBody>
      </Modal>
    </>
  );
};
